"use client";
import React, { useRef } from "react";
import styles from "./MessageItem.module.scss";
import { formatTimestamp } from "@/app/utils/date";
import Image from "next/image";
import { Message, MessageSender, MessageType } from "@/app/store/types";
import MsgStatus from "./MsgStatus";
import { useMessageActions } from "@/app/hooks/useMessageActions";
import MessageActionMenu from "./MessageActionMenu";
import CollapsibleMessage from "@/app/ui/collapsibleMessage";
import { useMessageRead } from "@/app/hooks/useMessageReaded";
import { getPublicImagePath } from "@/app/utils/image";
import { isForbiddenMsg, isWithdrawnMsg } from "@/app/store/formatMessage";
import Quoted, { QuotedFrom } from "./Quoted";
import Satisfy from "./modules/Satisfy";
import TextUi from "./modules/Text";
import RichTextUi from "./modules/RichText";
import ImageUi from "./modules/Image";
import AudioUi from "./modules/Audio";
import LineUp from "./modules/LineUp";
import VideoUi from "./modules/Video";
import FileUi from "./modules/File";
import MsgEndUi from "./modules/MsgEnd";
import Welcome from "./modules/Welcome";
import Template from "./modules/Template";
import QuestionList from "./modules/QuestionList";

interface IProps {
  msg: Message;
}

// 为每种消息类型预先定义懒加载组件
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const componentMap: Partial<Record<MessageType, React.ComponentType<any>>> = {
  [MessageType.Text]: TextUi,
  [MessageType.MessageAutoReply]: TextUi,
  [MessageType.IssueType]: TextUi,
  [MessageType.Image]: ImageUi,
  [MessageType.Audio]: AudioUi,
  [MessageType.LineUp]: LineUp,
  [MessageType.Video]: VideoUi,
  [MessageType.File]: FileUi,
  [MessageType.MsgEnd]: MsgEndUi,
  [MessageType.Welcome]: Welcome,
  [MessageType.Template]: Template,
  [MessageType.Satisfy]: Satisfy,
  [MessageType.QuestionList]: QuestionList,
  [MessageType.RichText]: RichTextUi,
  [MessageType.AI]: RichTextUi,
};

const getRenderComponent = (msg: Message): React.ReactNode => {
  const msgType = msg.type as MessageType;
  const LazyComponent =
    componentMap[msgType] || componentMap[MessageType.Text]!;

  return <LazyComponent msg={msg} />;
};

// 包含系统消息（排队、结束会话等），人工、用户消息
export default function MessageItem({ msg }: IProps) {
  const isOwnMessage = msg.senderType === MessageSender.USER;
  const menuRef = useRef<HTMLDivElement>(null);
  const {
    showMenu,
    messageRef,
    handleContextMenu,
    handleTouchStart,
    handleTouchEnd,
    handleWithdraw,
    handleQuoted,
    handleCopy,
    loading,
    canWithdraw,
  } = useMessageActions(msg, isOwnMessage, menuRef);

  // 上报用户已读
  const { itemRef } = useMessageRead(msg);

  // 特殊消息类型展示 排队、结束会话、满意度评价
  if (
    [MessageType.MsgEnd, MessageType.LineUp, MessageType.Satisfy].includes(
      msg.type as MessageType,
    )
  ) {
    return getRenderComponent(msg);
  }

  // 特殊消息类型展示 撤回、模版消息
  if (
    [MessageType.Template, MessageType.MessageSatisfyResult].includes(
      msg.type as MessageType,
    ) ||
    isWithdrawnMsg(msg)
  ) {
    if (MessageType.MessageSatisfyResult === msg.type && msg.disabled) {
      return <Satisfy msg={msg} />;
    }
    return <Template msg={msg} />;
  }

  const getName = () => {
    switch (msg.senderType) {
      case MessageSender.SYSTEM:
      case MessageSender.AGENT:
        return msg.senderName;
      default:
        return "";
    }
  };

  return (
    <div
      ref={itemRef}
      className={`${styles.message} ${styles[isOwnMessage ? "user" : "service"]}`}
    >
      <div className={styles.messageInfo}>
        <div className={styles.messageSender}>{getName()}</div>
        <div className={styles.messageTime}>
          {formatTimestamp(msg.timestamp)}
        </div>
      </div>
      {/* 引用消息 */}
      <Quoted
        quotedMsg={msg.quoteMessage as Message}
        msgSender={msg.senderType}
        from={QuotedFrom.Msg}
      />
      <div className={styles.messageInner}>
        <div className={styles.messageHeader}>
          <div className={styles.messageAvatar}>
            <Image
              src={getPublicImagePath("agentAvatar.svg")}
              alt="agentAvatar"
              width={36}
              height={36}
            />
          </div>
        </div>
        <div
          ref={messageRef}
          className={`${styles.messageContentContainer} ${msg.msgClassName ? styles[msg.msgClassName] : ""}`}
          onContextMenu={handleContextMenu}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchEnd}
        >
          {showMenu && (
            <div ref={menuRef}>
              <MessageActionMenu
                sender={msg.senderType}
                onWithdraw={handleWithdraw}
                onQuoted={handleQuoted}
                onCopy={() => handleCopy(msg)}
                loading={loading}
                canWithdraw={canWithdraw}
              />
            </div>
          )}
          <MsgStatus msg={msg} />

          <CollapsibleMessage
            // 系统消息不使用收起展开功能，系统消息往往需要交互，遮挡容易误解，且系统消息一般不会超出
            useCollapse={
              msg.senderType !== MessageSender.SYSTEM &&
              msg.type === MessageType.Text
            }
            maxHeight={240}
          >
            <div
              className={`${styles.messageContent} ${[MessageType.Video, MessageType.Audio, MessageType.Image].includes(msg.type as MessageType) && !isForbiddenMsg(msg) ? styles.file : ""}`}
              data-msg-type={msg.type}
            >
              {getRenderComponent(msg)}
            </div>
          </CollapsibleMessage>
        </div>
      </div>
    </div>
  );
}
